/**
 * Home page "Needs attention" + "Coming up". Pure, unit tested.
 */
import { houseName, tenantName } from "./ledger.js";

const DAY = 86400000;
const RENT_GRACE_DAYS = 5;

function startOfDay(now) {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

function ordinal(n) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${["st", "nd", "rd"][(n % 10) - 1] || "th"}`;
}

const fmtDate = (ts) => new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function needsAttention(
  { properties = [], tenants = [], rentPayments = [], maintenance = [], insurance = [], licenses = [], reminders = [] },
  now = new Date(),
) {
  const today = startOfDay(now);
  const propertiesById = new Map(properties.map((p) => [String(p._id), p]));
  const house = (id) => (id && propertiesById.get(String(id)) ? houseName(propertiesById.get(String(id))) : "");
  const items = [];

  // Rent not recorded this month past the due day (+ grace).
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  for (const tenant of tenants) {
    if (!tenant.isActive || !(tenant.monthlyRent > 0) || !propertiesById.has(String(tenant.propertyId))) continue;
    if (tenant.leaseEndDate && tenant.leaseEndDate < monthStart) continue;
    const dueDay = tenant.rentDueDay || 1;
    if (now.getDate() <= dueDay + RENT_GRACE_DAYS) continue;
    const paid = rentPayments.some(
      (payment) =>
        payment.status === "completed" &&
        payment.paymentDate >= monthStart &&
        (String(payment.tenantId) === String(tenant._id) ||
          (!payment.tenantId && String(payment.propertyId) === String(tenant.propertyId))),
    );
    if (!paid) {
      items.push({
        id: `rent-${tenant._id}`,
        tone: "urgent",
        rank: 0,
        title: `Rent not recorded · ${house(tenant.propertyId)}`,
        detail: `${tenantName(tenant)} · $${Number(tenant.monthlyRent).toLocaleString()} due on the ${ordinal(dueDay)}`,
        action: "Record rent",
        to: `/ledger?house=${tenant.propertyId}`,
      });
    }
  }

  for (const tenant of tenants) {
    if (!tenant.isActive || !tenant.leaseEndDate) continue;
    const days = Math.round((tenant.leaseEndDate - today) / DAY);
    if (days > 60 || days < -30) continue;
    items.push({
      id: `lease-${tenant._id}`,
      tone: days < 0 ? "urgent" : "soon",
      rank: days < 0 ? 1 : 3,
      title: `${days < 0 ? "Lease ended" : "Lease ends"} ${fmtDate(tenant.leaseEndDate)} · ${house(tenant.propertyId)}`,
      detail: tenantName(tenant),
      action: "Open house",
      to: `/houses/${tenant.propertyId}`,
    });
  }

  for (const policy of insurance) {
    const days = Math.round((policy.termEnd - today) / DAY);
    if (!Number.isFinite(days) || days > 30 || days < -60) continue;
    items.push({
      id: `insurance-${policy._id}`,
      tone: days < 0 ? "urgent" : "soon",
      rank: days < 0 ? 1 : 2,
      title: `Insurance ${days < 0 ? "expired" : "renews"} ${fmtDate(policy.termEnd)} · ${house(policy.propertyId) || policy.propertyLabel}`,
      detail: policy.insuranceName,
      action: "Review",
      to: "/insurance",
    });
  }

  for (const license of licenses) {
    const days = Math.round((license.dateTo - today) / DAY);
    if (!Number.isFinite(days) || days > 30 || days < -60) continue;
    items.push({
      id: `license-${license._id}`,
      tone: days < 0 ? "urgent" : "soon",
      rank: days < 0 ? 1 : 2,
      title: `Rental license ${days < 0 ? "expired" : "expires"} ${fmtDate(license.dateTo)} · ${house(license.propertyId) || license.propertyLabel}`,
      detail: license.category,
      action: "Review",
      to: "/insurance",
    });
  }

  for (const job of maintenance) {
    if (["completed", "cancelled"].includes(job.status)) continue;
    const days = Math.floor((today - (job.createdAt || today)) / DAY);
    if (days < 3) continue;
    items.push({
      id: `job-${job._id}`,
      tone: ["urgent", "emergency"].includes(job.priority) ? "urgent" : "soon",
      rank: ["urgent", "emergency"].includes(job.priority) ? 0 : 4,
      title: `${job.title} · ${house(job.propertyId)}`,
      detail: `Open ${days} days`,
      action: "Open",
      to: "/maintenance",
    });
  }

  for (const reminder of reminders) {
    if (reminder.isCompleted || !Number.isFinite(reminder.dueAt) || reminder.dueAt >= today + DAY) continue;
    const overdue = reminder.dueAt < today;
    items.push({
      id: `todo-${reminder._id}`,
      tone: overdue ? "urgent" : "soon",
      rank: overdue ? 1 : 2,
      title: `${reminder.title}${house(reminder.propertyId) ? ` · ${house(reminder.propertyId)}` : ""}`,
      detail: overdue ? `To-do · was due ${fmtDate(reminder.dueAt)}` : "To-do · due today",
      action: "Done",
      reminderId: reminder._id,
    });
  }

  return items.sort((a, b) => a.rank - b.rank);
}

/** Everything that happened lately, newest first: money, repairs, to-dos. */
export function recentActivity(
  { properties = [], expenses = [], rentPayments = [], maintenance = [], reminders = [], tenants = [] },
  limit = 8,
) {
  const propertiesById = new Map(properties.map((p) => [String(p._id), p]));
  const tenantsById = new Map(tenants.map((t) => [String(t._id), t]));
  const house = (id) => (id && propertiesById.get(String(id)) ? houseName(propertiesById.get(String(id))) : "");
  const items = [];
  for (const e of expenses) {
    items.push({ id: `e-${e._id}`, at: e.createdAt || e.date, type: "Expense", title: e.title, where: house(e.propertyId) || "Business", amount: -Math.abs(Number(e.amount || 0)), to: "/ledger?m=all" });
  }
  for (const r of rentPayments) {
    if (r.status !== "completed") continue;
    const t = r.tenantId && tenantsById.get(String(r.tenantId));
    items.push({ id: `r-${r._id}`, at: r.createdAt || r.paymentDate, type: "Rent", title: t ? `Rent from ${tenantName(t)}` : "Rent received", where: house(r.propertyId), amount: Math.abs(Number(r.amount || 0)), to: "/ledger?m=all" });
  }
  for (const m of maintenance) {
    items.push({ id: `m-${m._id}`, at: m.createdAt, type: "Repair logged", title: m.title, where: house(m.propertyId), to: "/maintenance" });
    if (m.status === "completed" && m.completedDate) {
      items.push({ id: `md-${m._id}`, at: m.completedDate, type: "Repair done", title: m.title, where: house(m.propertyId), to: "/maintenance" });
    }
  }
  for (const r of reminders) {
    items.push({ id: `t-${r._id}`, at: r.createdAt, type: r.isCompleted ? "To-do done" : "To-do added", title: r.title, where: house(r.propertyId), due: r.isCompleted ? null : r.dueAt });
  }
  return items.filter((item) => Number.isFinite(item.at)).sort((a, b) => b.at - a.at).slice(0, limit);
}

export function comingUp(
  { properties = [], tenants = [], insurance = [], licenses = [], reminders = [], maintenance = [] },
  now = new Date(),
  horizonDays = 90,
) {
  const today = startOfDay(now);
  const propertiesById = new Map(properties.map((p) => [String(p._id), p]));
  const house = (id, fallback) => (id && propertiesById.get(String(id)) ? houseName(propertiesById.get(String(id))) : fallback || "");
  const items = [
    ...tenants
      .filter((t) => t.isActive && t.leaseEndDate)
      .map((t) => ({ id: `lease-${t._id}`, date: t.leaseEndDate, title: "Lease ends", where: house(t.propertyId) })),
    ...insurance.map((p) => ({ id: `ins-${p._id}`, date: p.termEnd, title: `${p.insuranceName} renewal`, where: house(p.propertyId, p.propertyLabel) })),
    ...licenses.map((l) => ({ id: `lic-${l._id}`, date: l.dateTo, title: "Rental license", where: house(l.propertyId, l.propertyLabel) })),
    ...reminders
      .filter((r) => !r.isCompleted)
      .map((r) => ({ id: `todo-${r._id}`, date: r.dueAt, title: r.title, where: house(r.propertyId), reminderId: r._id, todo: true })),
    ...maintenance
      .filter((m) => m.scheduledDate && !["completed", "cancelled"].includes(m.status))
      .map((m) => ({ id: `job-${m._id}`, date: m.scheduledDate, title: `Repair: ${m.title}`, where: house(m.propertyId), to: "/maintenance" })),
  ];
  return items
    .filter((item) => Number.isFinite(item.date) && item.date >= today && item.date <= today + horizonDays * DAY)
    .sort((a, b) => a.date - b.date);
}
